/**
 * Base locale IndexedDB (Dexie) — source de vérité offline.
 *
 * Schéma v1 — aligné avec la couche D1 serveur (colonnes en snake_case pour le push).
 * La sync (push) enverra ces tables au Worker Core via POST /sync/push.
 */
import Dexie, { type Table } from 'dexie';

export interface ArticleRow {
  id: string;
  nom: string;
  categorie?: string;
  prix_vente: number;       // FCFA entier
  cout_achat?: number;
  stock_actuel: number;
  seuil_rupture: number;
  en_vitrine: 0 | 1;
  description?: string;
  updated_at: number;
  deleted_at?: number | null;
  _sync: 'pending' | 'synced';
}

export interface ClientRow {
  id: string;
  nom: string;
  tel?: string;
  scoring: number;          // 0..100
  nb_visites: number;
  total_achats: number;
  bloque: 0 | 1;
  motif_blocage?: string;
  derniere_visite_at?: number;
  updated_at: number;
  deleted_at?: number | null;
  _sync: 'pending' | 'synced';
}

export interface VenteRow {
  id: string;               // ULID généré client (idempotence)
  user_id: string;
  client_id?: string;
  lignes: Array<{ articleId: string; nom: string; prixUnitaire: number; qte: number; poidsKg?: number }>;
  paiement: Array<{ mode: 'cash' | 'momo' | 'orange' | 'moov' | 'credit'; montant: number; momoRef?: string }>;
  total: number;
  mode: 'cash' | 'momo' | 'mixte' | 'credit' | string;
  monnaie: number;
  part_credit: number;
  statut: 'valide' | 'annulee';
  motif_annulation?: string;
  created_at: number;
  updated_at: number;
  _sync: 'pending' | 'synced';
}

export interface CreditRow {
  id: string;
  client_id: string;
  vente_id: string;
  montant: number;
  paye: number;
  echeance_at: number;
  statut: 'ouvert' | 'relance' | 'bloque' | 'paye';
  scoring_au_moment: number;
  updated_at: number;
  deleted_at?: number | null;
  _sync: 'pending' | 'synced';
}

export interface SyncOpRow {
  id?: number;              // autoincrement
  table: 'articles' | 'clients' | 'ventes' | 'credits' | 'paiements_credit' | 'stock_mouvements';
  entity_id: string;
  op: 'create' | 'update' | 'delete';
  payload: unknown;
  ts_appareil: number;
  retries: number;
}

export class BoutikDB extends Dexie {
  articles!: Table<ArticleRow, string>;
  clients!: Table<ClientRow, string>;
  ventes!: Table<VenteRow, string>;
  credits!: Table<CreditRow, string>;
  sync_queue!: Table<SyncOpRow, number>;

  constructor() {
    super('boutikos');
    this.version(1).stores({
      articles: 'id, nom, categorie, _sync, updated_at',
      clients: 'id, nom, tel, scoring, _sync, updated_at',
      ventes: 'id, client_id, statut, created_at, _sync',
      credits: 'id, client_id, vente_id, statut, echeance_at, _sync',
      sync_queue: '++id, table, entity_id, ts_appareil',
    });
  }
}

export const db = new BoutikDB();
